import { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import en from './en.json';
import es from './es.json';
import fr from './fr.json';

export type Language = 'en' | 'es' | 'fr';

const translations: Record<Language, typeof en> = { en, es, fr };

const STORAGE_KEY = 'tellowup-lang';
const SUPPORTED_LANGS: Language[] = ['en', 'es', 'fr'];

function getStoredLang(): Language | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGS.includes(stored as Language)) {
      return stored as Language;
    }
  } catch {}
  return null;
}

interface I18nContextType {
  language: Language;
  lang: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, fallback?: string) => string;
  tObj: (key: string) => Record<string, unknown>;
}

const I18nContext = createContext<I18nContextType | null>(null);

interface I18nProviderProps {
  children: ReactNode;
  forceLang?: Language;
}

export function I18nProvider({ children, forceLang }: I18nProviderProps) {
  // Priority: stored preference > URL param (forceLang) > browser locale > 'en'
  const [language, setLanguage] = useState<Language>(() => {
    if (forceLang && forceLang in translations) return forceLang;
    const stored = getStoredLang();
    if (stored) return stored;
    const browserLang = navigator.language.split('-')[0];
    if (SUPPORTED_LANGS.includes(browserLang as Language)) {
      return browserLang as Language;
    }
    return 'en';
  });

  // Persist language choice to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch {}
  }, [language]);

  // When URL lang changes, update language (user navigated via URL directly)
  const prevForceLang = useRef<Language | undefined>(undefined);
  useEffect(() => {
    if (forceLang !== prevForceLang.current && forceLang && forceLang in translations && language !== forceLang) {
      setLanguage(forceLang);
    }
    prevForceLang.current = forceLang;
  }, [forceLang, language]);

  const t = useCallback((key: string, fallback?: string): string => {
    const keys = key.split('.');
    let value: unknown = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = (value as Record<string, unknown>)[k];
      } else {
        return fallback ?? key;
      }
    }

    return typeof value === 'string' ? value : (fallback ?? key);
  }, [language]);

  const tObj = useCallback((key: string): Record<string, unknown> => {
    const keys = key.split('.');
    let value: unknown = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = (value as Record<string, unknown>)[k];
      } else {
        console.warn(`Translation key not found: ${key}`);
        return {};
      }
    }

    return typeof value === 'object' && value !== null ? value as Record<string, unknown> : {};
  }, [language]);

  return (
    <I18nContext.Provider value={{ language, lang: language, setLanguage, t, tObj }}>
      {children}
    </I18nContext.Provider>
  );
}

// Separate export for use with useParams - wraps App component
export function I18nProviderContent({ forceLang, children }: I18nProviderProps) {
  return (
    <I18nProvider forceLang={forceLang}>
      {children}
    </I18nProvider>
  );
}

export function useTranslation() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within I18nProvider');
  }
  return context;
}

export const languages: { code: Language; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
];